import { cn } from "@/utils/cn";
import { GameZone } from "./gameTypes";
import { formatCurrency } from "./gameUtils";

const COLOR_MAP: Record<
  string,
  { border: string; bg: string; text: string; glow: string; badge: string }
> = {
  red:    { border: "border-red-500",    bg: "bg-red-950/40",    text: "text-red-400",    glow: "shadow-[0_0_16px_rgba(239,68,68,0.3)]",   badge: "bg-red-500" },
  yellow: { border: "border-yellow-500", bg: "bg-yellow-950/40", text: "text-yellow-400", glow: "shadow-[0_0_16px_rgba(234,179,8,0.3)]",    badge: "bg-yellow-500" },
  blue:   { border: "border-blue-500",   bg: "bg-blue-950/40",   text: "text-blue-400",   glow: "shadow-[0_0_16px_rgba(59,130,246,0.3)]",   badge: "bg-blue-500" },
  purple: { border: "border-purple-500", bg: "bg-purple-950/40", text: "text-purple-400", glow: "shadow-[0_0_16px_rgba(168,85,247,0.3)]",   badge: "bg-purple-500" },
  green:  { border: "border-emerald-500",bg: "bg-emerald-950/40",text: "text-emerald-400",glow: "shadow-[0_0_16px_rgba(52,211,153,0.3)]",   badge: "bg-emerald-500" },
  gray:   { border: "border-gray-700",   bg: "bg-gray-900/60",   text: "text-gray-500",   glow: "",                                          badge: "bg-gray-600" },
};

interface GameWorldMapProps {
  zones: GameZone[];
}

export function GameWorldMap({ zones }: GameWorldMapProps) {
  const pixelFont: React.CSSProperties = { fontFamily: "'Press Start 2P', monospace" };

  return (
    <div className="p-4 h-full overflow-y-auto">
      <div className="mb-4 text-center" style={pixelFont}>
        <span className="text-emerald-500 text-[8px] tracking-widest">
          ▶ MAPA DO MUNDO — PIPELINE DE COMPRAS ◀
        </span>
      </div>

      {/* Map grid — 5 zones connected by arrows */}
      <div className="flex flex-col gap-3 max-w-2xl mx-auto">
        {zones.map((zone, idx) => {
          const colors = COLOR_MAP[zone.colorKey] ?? COLOR_MAP.gray;
          return (
            <div key={zone.id}>
              <div
                className={cn(
                  "border-2 p-4 transition-all",
                  colors.border,
                  colors.bg,
                  colors.glow,
                  zone.isBoss && zone.count > 0 && "animate-pulse"
                )}
              >
                <div className="flex items-start gap-4">
                  {/* Zone icon */}
                  <div className="flex-shrink-0 text-4xl leading-none">{zone.emoji}</div>

                  {/* Zone info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={cn("text-[8px] font-bold", colors.text)} style={pixelFont}>
                        {zone.name}
                      </span>
                      {zone.isBoss && zone.count > 0 && (
                        <span
                          className="text-[7px] bg-red-600 text-white px-1.5 py-0.5"
                          style={pixelFont}
                        >
                          ⚔ BOSS
                        </span>
                      )}
                      {zone.isSafe && (
                        <span
                          className="text-[7px] bg-emerald-700 text-white px-1.5 py-0.5"
                          style={pixelFont}
                        >
                          ✓ SEGURO
                        </span>
                      )}
                    </div>

                    <p className="text-gray-500 text-[9px] mb-2 font-mono">{zone.description}</p>

                    <div className="flex items-center gap-4 flex-wrap">
                      {/* Monster count */}
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600 text-[9px] font-mono">ITENS:</span>
                        <span
                          className={cn(
                            "text-[10px] font-bold px-2 py-0.5",
                            colors.badge,
                            "text-white"
                          )}
                          style={pixelFont}
                        >
                          {zone.count}
                        </span>
                      </div>

                      {/* Value if available */}
                      {zone.totalValue > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="text-gray-600 text-[9px] font-mono">VALOR:</span>
                          <span className={cn("text-[9px] font-mono font-bold", colors.text)}>
                            {formatCurrency(zone.totalValue)}
                          </span>
                        </div>
                      )}

                      {/* Empty zone indicator */}
                      {zone.count === 0 && (
                        <span className="text-gray-700 text-[9px] font-mono italic">
                          — zona vazia —
                        </span>
                      )}
                    </div>

                    {/* Progress bar (monsters as hp-like bar) */}
                    {zone.count > 0 && (
                      <div className="mt-2">
                        <div className="h-1.5 bg-gray-800 w-full overflow-hidden">
                          <div
                            className={cn("h-full transition-all", colors.badge)}
                            style={{ width: `${Math.min(100, zone.count * 5)}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Status indicator */}
                  <div className="flex-shrink-0 flex flex-col items-center gap-1">
                    <div
                      className={cn(
                        "w-3 h-3 rounded-full",
                        zone.count === 0
                          ? "bg-gray-700"
                          : zone.isBoss
                            ? "bg-red-500 animate-pulse"
                            : zone.isSafe
                              ? "bg-emerald-500"
                              : zone.colorKey === "yellow"
                                ? "bg-yellow-500 animate-pulse"
                                : colors.badge
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Arrow connector (not after last) */}
              {idx < zones.length - 1 && (
                <div className="flex justify-center my-1">
                  <span className="text-gray-700 text-xs font-mono">▼</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-6 max-w-2xl mx-auto border border-gray-800 p-3">
        <div className="text-gray-600 text-[8px] mb-2" style={pixelFont}>LEGENDA</div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          {[
            { color: "bg-emerald-500", label: "Zona segura / finalizado" },
            { color: "bg-yellow-500", label: "Atenção / pendente" },
            { color: "bg-red-500",     label: "Crítico / boss / bloqueado" },
            { color: "bg-blue-500",    label: "Em andamento" },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-2">
              <div className={cn("w-2 h-2 rounded-full flex-shrink-0", item.color)} />
              <span className="text-gray-600 text-[8px] font-mono">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
